<template>
  <div ref="container" class="top-search-autocomplete">
    <div class="top-search-autocomplete__field">
      <input
        ref="input"
        v-model="query"
        class="top-search form-control"
        data-testid="top-search-input"
        type="search"
        placeholder="Input film or show title, or person name..."
        @input="handleInput"
        @focus="handleFocus"
        @keydown.down.prevent="highlightNextResult"
        @keydown.up.prevent="highlightPreviousResult"
        @keydown.enter.prevent="handleEnter"
        @keydown.esc="handleEscape"
      >
      <div
        v-if="showResults"
        class="top-search-autocomplete__results"
        data-testid="top-search-results"
      >
        <a
          v-for="(result, index) in results"
          :key="`${result.entity_type}-${result.url}-${result.keyword}`"
          :ref="(element) => setResultRef(element, index)"
          :href="result.url"
          :class="[
            'top-search-autocomplete__result',
            { 'top-search-autocomplete__result--active': index === activeIndex },
          ]"
          data-testid="top-search-result"
          @mouseenter="setActiveIndex(index)"
          @focus="setActiveIndex(index)"
          @click="handleResultClick(result, index)"
        >
          <span class="top-search-autocomplete__label">
            <span class="top-search-autocomplete__keyword">{{ result.keyword }}</span>
            <span
              v-if="shouldShowResultYear(result)"
              class="top-search-autocomplete__year"
            >
              ({{ result.year }})
            </span>
          </span>
          <span
            v-if="!result.is_all_results"
            class="top-search-autocomplete__type"
          >
            {{ result.entity_type }}
          </span>
        </a>
      </div>
    </div>
    <button
      class="search-btn"
      data-testid="top-search-button"
      type="button"
      aria-label="Search"
      @click="submitSearch"
    >
      <i class="ion-android-search"></i>
    </button>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { trackSearchInteraction } from '../analytics/searchInteractions'

const props = defineProps({
  autofocus: {
    type: Boolean,
    default: false,
  },
  source: {
    type: String,
    default: 'site_quick_search',
  },
})

const query = ref('')
const results = ref([])
const container = ref(null)
const input = ref(null)
const activeIndex = ref(-1)
const resultRefs = []
let activeRequestId = 0
let searchTimer = null
let activeAbortController = null
let interactionLogged = false

const SEARCH_DEBOUNCE_MS = 250

const normalizedQuery = computed(() => query.value.trim())
const showResults = computed(() => normalizedQuery.value.length >= 2 && results.value.length > 0)

function shouldShowResultYear(result) {
  return Boolean(result?.year && ['Film', 'Show'].includes(result?.entity_type))
}

function clearResults() {
  results.value = []
  activeIndex.value = -1
  resultRefs.length = 0
}

function clearSearchTimer() {
  if (searchTimer) {
    window.clearTimeout(searchTimer)
    searchTimer = null
  }
}

function abortActiveSearchRequest() {
  if (activeAbortController) {
    activeAbortController.abort()
    activeAbortController = null
  }
}

function cancelPendingSearch() {
  clearSearchTimer()
  abortActiveSearchRequest()
  activeRequestId += 1
}

function recordSearchInteraction(action, result = null, index = null) {
  const searchQuery = normalizedQuery.value
  if (searchQuery.length < 2 || interactionLogged) {
    return
  }

  interactionLogged = true
  trackSearchInteraction({
    search_query: searchQuery,
    source: props.source,
    action,
    entity_type: result?.entity_type || '',
    entity_id: result?.id || null,
    result_position: index === null ? null : index + 1,
    results_count: results.value.length,
  })
}

function clearSearch(options = {}) {
  if (options.trackAbandoned) {
    recordSearchInteraction('abandoned')
  }
  cancelPendingSearch()
  clearResults()
}

function setResultRef(element, index) {
  if (!element) {
    return
  }
  resultRefs[index] = element
}

function setActiveIndex(index) {
  activeIndex.value = index
  scrollActiveResultIntoView()
}

async function scrollActiveResultIntoView() {
  await nextTick()
  const activeElement = resultRefs[activeIndex.value]
  if (typeof activeElement?.scrollIntoView === 'function') {
    activeElement.scrollIntoView({ block: 'nearest' })
  }
}

function highlightNextResult() {
  if (!showResults.value) {
    return
  }
  const nextIndex = activeIndex.value >= results.value.length - 1 ? 0 : activeIndex.value + 1
  setActiveIndex(nextIndex)
}

function highlightPreviousResult() {
  if (!showResults.value) {
    return
  }
  const previousIndex = activeIndex.value <= 0 ? results.value.length - 1 : activeIndex.value - 1
  setActiveIndex(previousIndex)
}

async function fetchResults(value) {
  abortActiveSearchRequest()
  const requestId = ++activeRequestId
  const abortController = typeof AbortController === 'undefined'
    ? null
    : new AbortController()
  activeAbortController = abortController
  try {
    const requestOptions = {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    }
    if (abortController) {
      requestOptions.signal = abortController.signal
    }
    const response = await fetch(`/search/suggest/?q=${encodeURIComponent(value)}`, requestOptions)
    if (!response.ok) {
      if (requestId === activeRequestId) {
        clearResults()
      }
      return
    }

    const payload = await response.json()
    if (requestId !== activeRequestId || normalizedQuery.value !== value) {
      return
    }

    results.value = Array.isArray(payload.results) ? payload.results : []
    activeIndex.value = -1
    resultRefs.length = 0
  } catch (error) {
    if (error?.name === 'AbortError') {
      return
    }
    if (requestId === activeRequestId) {
      clearResults()
    }
  } finally {
    if (requestId === activeRequestId && activeAbortController === abortController) {
      activeAbortController = null
    }
  }
}

function scheduleFetchResults(value) {
  clearSearchTimer()
  searchTimer = window.setTimeout(() => {
    searchTimer = null
    fetchResults(value)
  }, SEARCH_DEBOUNCE_MS)
}

function handleInput() {
  const nextQuery = normalizedQuery.value
  interactionLogged = false
  if (nextQuery.length < 2) {
    clearSearch()
    return
  }
  scheduleFetchResults(nextQuery)
}

function handleFocus() {
  if (normalizedQuery.value.length >= 2 && results.value.length === 0) {
    scheduleFetchResults(normalizedQuery.value)
  }
}

function handleEnter() {
  if (activeIndex.value >= 0 && resultRefs[activeIndex.value]) {
    resultRefs[activeIndex.value].click()
    return
  }
  submitSearch()
}

function handleDocumentClick(event) {
  if (!container.value?.contains(event.target)) {
    clearSearch({ trackAbandoned: true })
  }
}

function handleEscape() {
  clearSearch({ trackAbandoned: true })
}

function handleResultClick(result, index) {
  recordSearchInteraction('selected_result', result, index)
  clearSearch()
}

function submitSearch() {
  if (!normalizedQuery.value || typeof window === 'undefined') {
    return
  }
  const searchUrl = `/search?q=${encodeURIComponent(normalizedQuery.value)}`
  recordSearchInteraction('submitted', { entity_type: 'Search', url: searchUrl })
  cancelPendingSearch()
  window.location.assign(searchUrl)
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick)
  if (props.autofocus) {
    input.value?.focus()
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
  recordSearchInteraction('abandoned')
  cancelPendingSearch()
})
</script>
